import React, { useMemo, useState } from 'react';
import { format, isSameMonth, parseISO } from 'date-fns';
import { ArrowRightIcon, BellDotIcon, EuroIcon, GaugeIcon, WalletIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useStudio } from '../../contexts/StudioContext';
import { Booking } from '../../types/booking';
import { ACTIVE_STATUSES, bookingsForDate, utilisationForDate } from '../../utils/availability';
import { euro, longDate, shortDate, timeRange } from '../../utils/format';
import { BookingDetail } from '../../components/admin/BookingDetail';
import { StatusBadge } from '../../components/admin/StatusBadge';
export function Dashboard() {
  const {
    bookings,
    services,
    settings
  } = useStudio();
  const [open, setOpen] = useState<Booking | null>(null);
  const todayIso = format(new Date(), 'yyyy-MM-dd');
  const stats = useMemo(() => {
    const month = bookings.filter(b => isSameMonth(parseISO(b.date), new Date()) && ACTIVE_STATUSES.includes(b.status));
    return {
      revenue: month.reduce((sum, b) => sum + b.amount, 0),
      monthCount: month.length,
      deposits: bookings.filter(b => b.paymentStatus === 'deposit_paid').reduce((sum, b) => sum + b.depositAmount, 0),
      openRequests: bookings.filter(b => b.status === 'pending').length,
      utilisation: utilisationForDate(bookings, todayIso, settings)
    };
  }, [bookings, settings, todayIso]);
  const today = bookingsForDate(bookings, todayIso).filter(b => b.status !== 'cancelled');
  const recent = [...bookings].sort((a, b) => a.createdAt < b.createdAt ? 1 : -1).slice(0, 5);
  const cards = [{
    icon: EuroIcon,
    label: 'Omzet deze maand',
    value: euro(stats.revenue),
    note: `${stats.monthCount} afspraken`
  }, {
    icon: WalletIcon,
    label: 'Aanbetalingen',
    value: euro(stats.deposits),
    note: 'Vooruit ontvangen'
  }, {
    icon: BellDotIcon,
    label: 'Open aanvragen',
    value: String(stats.openRequests),
    note: 'Te bevestigen'
  }, {
    icon: GaugeIcon,
    label: 'Bezetting vandaag',
    value: `${stats.utilisation}%`,
    note: `${settings.bays} plekken tegelijk`
  }];
  return <div className="w-full max-w-full px-4 py-5 lg:px-8 lg:py-8" data-id="element-914" data-mp="106cvvpnok6pd">
      <header className="lg:flex lg:items-end lg:justify-between lg:gap-6" data-id="element-915" data-mp="1f4yglecjfcjl">
        <div data-id="element-916" data-mp="1a5c2l2ovxs9q">
          <p className="text-[11px] uppercase tracking-[0.18em] text-ember" data-id="element-917" data-mp="1a4sl1n35d5w8">Vandaag</p>
          <h2 className="mt-1 font-display text-xl font-extrabold capitalize leading-tight tracking-tightest text-chalk lg:text-3xl" data-id="element-918" data-mp="mfranuar7kob">
            {longDate(todayIso)}
          </h2>
        </div>
        <Link to="/admin/agenda" className="mt-4 hidden items-center gap-2 rounded-full bg-panel px-5 py-2.5 text-sm text-chalk transition-colors duration-200 ease-smooth hover:text-ember lg:mt-0 lg:inline-flex" data-id="element-919" data-mp="w5kkaprc5ppx">
          Naar agenda
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-920" data-mp="3nf7j5ca0fd4" />
        </Link>
      </header>

      <div className="mt-5 grid grid-cols-2 gap-3 lg:mt-7 lg:grid-cols-4 lg:gap-5" data-id="element-921" data-mp="3uv1fvb9xx60b">
        {cards.map(card => {
        const Icon = card.icon;
        return <div key={card.label} className="rounded-2xl border border-edge bg-carbon px-3.5 py-3.5 lg:rounded-3xl lg:px-5 lg:py-5" data-id="element-922" data-mp="2f5acmmalun6l">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-ember/15 text-ember lg:h-9 lg:w-9 lg:rounded-xl" data-id="element-923" data-mp="25e5vph1dcs94">
                <Icon className="h-4 w-4" aria-hidden="true" data-id="element-924" data-mp="2t5bbjw7b7kjf" />
              </span>
              <span className="mt-2.5 block font-display text-xl font-extrabold leading-tight tracking-tight text-chalk lg:text-3xl" data-id="element-925" data-mp="2y3csaah9j7rq">
                {card.value}
              </span>
              <span className="mt-1 block text-[11px] leading-snug text-dim lg:text-xs" data-id="element-926" data-mp="2o4dvbp5v10kk">{card.label}</span>
              <span className="mt-0.5 hidden text-xs text-dim/70 lg:block" data-id="element-927" data-mp="18nwyq3ftnumy">{card.note}</span>
            </div>;
      })}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:mt-3 lg:grid-cols-2 lg:gap-6" data-id="element-928" data-mp="1cr33t7r77fru">
        <section className="mt-7 lg:mt-4" data-id="element-929" data-mp="1sxocnjihve0r">
          <div className="flex items-center justify-between gap-3" data-id="element-930" data-mp="1o66cgxoaphgu">
            <h3 className="text-[11px] uppercase tracking-[0.18em] text-dim lg:text-xs" data-id="element-931" data-mp="1o4v92yyribq9">
              In de werkplaats vandaag
            </h3>
            <Link to="/admin/agenda" className="flex items-center gap-1 text-[11px] text-ember lg:text-xs" data-id="element-932" data-mp="1e8a42dmqt0mf">
              Agenda
              <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-933" data-mp="2bupl5eb9bw9g" />
            </Link>
          </div>

          {today.length > 0 ? <ul className="mt-3 space-y-2.5" data-id="element-934" data-mp="26uuth8jnr2nc">
              {today.map(booking => {
            const service = services.find(s => s.id === booking.serviceId);
            return <li key={booking.id} data-id="element-935" data-mp="1xy2eukxeztqv">
                    <button type="button" onClick={() => setOpen(booking)} className="block w-full rounded-2xl border border-edge bg-carbon p-4 text-left transition-colors duration-200 ease-smooth hover:border-ember/60 active:border-ember" data-id="element-936" data-mp="qio5nse72pfh">
                      <span className="flex items-center justify-between gap-3" data-id="element-937" data-mp="326au5mj18i4y">
                        <span className="font-display text-sm font-extrabold text-ember" data-id="element-938" data-mp="373t4nhcp75f2">
                          {timeRange(booking.time, booking.durationMinutes)}
                        </span>
                        <StatusBadge status={booking.status} data-id="element-939" data-mp="3gtm63pj4m45l" />
                      </span>
                      <span className="mt-2 flex items-start justify-between gap-3" data-id="element-940" data-mp="3lsz1wzrz6lkb">
                        <span className="break-words font-medium text-chalk" data-id="element-941" data-mp="3puu3m5dtj0ym">{booking.customer.name}</span>
                        <span className="shrink-0 font-display text-base font-extrabold text-chalk" data-id="element-942" data-mp="3uuotq6prrmim">
                          {euro(booking.amount)}
                        </span>
                      </span>
                      <span className="mt-0.5 block break-words text-sm text-dim" data-id="element-943" data-mp="20hgu81qummqn">
                        {service?.name} · {booking.customer.car}
                      </span>
                    </button>
                  </li>;
          })}
            </ul> : <p className="mt-3 rounded-2xl border border-dashed border-edge px-4 py-6 text-sm text-dim" data-id="element-944" data-mp="3cs9mimcwfduc">
              Geen afspraken vandaag.
            </p>}
        </section>

        <section className="mt-7 lg:mt-4" data-id="element-945" data-mp="3mpnqvnljsc6a">
          <div className="flex items-center justify-between gap-3" data-id="element-946" data-mp="3momg7k8g1rp3">
            <h3 className="text-[11px] uppercase tracking-[0.18em] text-dim lg:text-xs" data-id="element-947" data-mp="3hrvzqvsq8u35">Laatste boekingen</h3>
            <Link to="/admin/bookings" className="flex items-center gap-1 text-[11px] text-ember lg:text-xs" data-id="element-948" data-mp="3vhfxtobd5364">
              Alles
              <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-949" data-mp="3rhp87nxkot36" />
            </Link>
          </div>

          <ul className="mt-3 space-y-2.5" data-id="element-950" data-mp="996hsincabsw">
            {recent.map(booking => <li key={booking.id} data-id="element-951" data-mp="4e10jx4vtlb5">
                <button type="button" onClick={() => setOpen(booking)} className="block w-full rounded-2xl border border-edge bg-carbon p-4 text-left transition-colors duration-200 ease-smooth hover:border-ember/60 active:border-ember" data-id="element-952" data-mp="ny9diwo0c78f">
                  <span className="flex items-start justify-between gap-3" data-id="element-953" data-mp="iydi3ltdlo4h">
                    <span className="break-words font-medium text-chalk" data-id="element-954" data-mp="57i640nkyqwo">{booking.customer.name}</span>
                    <span className="shrink-0 font-display text-base font-extrabold text-chalk" data-id="element-955" data-mp="9y8c9g3tyxsf">
                      {euro(booking.amount)}
                    </span>
                  </span>
                  <span className="mt-1.5 flex items-center justify-between gap-3" data-id="element-956" data-mp="8f0adzs0gnn">
                    <span className="text-sm text-dim" data-id="element-957" data-mp="6b129cto5tp">
                      {shortDate(booking.date)} · {booking.time}
                    </span>
                    <StatusBadge status={booking.status} data-id="element-958" data-mp="3mfvg71jcjgrp" />
                  </span>
                </button>
              </li>)}
          </ul>
        </section>
      </div>

      {open && <BookingDetail booking={open} onClose={() => setOpen(null)} data-id="element-959" data-mp="1q499fz66871" />}
    </div>;
}